import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Box,
  Center,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconBrandLinkedin } from '@tabler/icons-react';
import useApi from '../hooks/useApi';
import { fetchOptOuts } from '../services/api';

function initialsFor(name) {
  const parts = String(name || '').trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

const CHANNEL_ICON = {
  'LinkedIn DM': <IconBrandLinkedin size={13} />,
};

export default function AviaryV2OptOutsPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchOptOuts(api)
      .then((data) => { if (!cancelled) setRows(data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || err.message); });
    return () => { cancelled = true; };
  }, [api]);

  if (error) {
    return (
      <Center py={80} px="lg">
        <Stack gap="sm" align="center">
          <Text fw={600} c="red">Couldn't load opt-outs</Text>
          <Text size="sm" c="dimmed">{error}</Text>
        </Stack>
      </Center>
    );
  }

  if (rows === null) {
    return (
      <Center py={80}>
        <Loader color="sage" />
      </Center>
    );
  }

  return (
    <Box style={{ padding: '40px 24px 120px', maxWidth: 900, margin: '0 auto' }}>
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 28 }}>
            Opt-outs
          </Text>
          <Text size="sm" c="dimmed">{rows.length} contact{rows.length !== 1 ? 's' : ''}</Text>
        </Group>

        {rows.length === 0 ? (
          <Paper p="xl" radius="md" withBorder style={{ background: 'var(--aviary-surface)' }}>
            <Center py={40}>
              <Stack gap="xs" align="center">
                <Text fw={500} style={{ color: 'var(--aviary-ink)' }}>No opt-outs yet</Text>
                <Text size="sm" c="dimmed">Contacts who reply with a stop word or click an unsubscribe link will appear here.</Text>
              </Stack>
            </Center>
          </Paper>
        ) : (
          <Paper radius="md" withBorder style={{ background: 'var(--aviary-surface)', overflow: 'hidden' }}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr style={{ background: 'var(--mantine-color-gray-0)' }}>
                  <Table.Th style={{ fontSize: 12, color: 'var(--aviary-muted)', fontWeight: 600, letterSpacing: 0.4 }}>CONTACT</Table.Th>
                  <Table.Th style={{ fontSize: 12, color: 'var(--aviary-muted)', fontWeight: 600, letterSpacing: 0.4 }}>CHANNEL</Table.Th>
                  <Table.Th style={{ fontSize: 12, color: 'var(--aviary-muted)', fontWeight: 600, letterSpacing: 0.4 }}>REASON</Table.Th>
                  <Table.Th style={{ fontSize: 12, color: 'var(--aviary-muted)', fontWeight: 600, letterSpacing: 0.4 }}>DATE</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => (
                  <UnstyledButton
                    key={`${row.leadId}-${row.closedAt}`}
                    component={Table.Tr}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/v2/leads/${row.leadId}`)}
                  >
                    <Table.Td py="sm">
                      <Group gap="sm" wrap="nowrap">
                        <Avatar
                          src={row.photoUrl || null}
                          size={32}
                          radius="xl"
                          color="gray"
                        >
                          {initialsFor(row.name)}
                        </Avatar>
                        <Stack gap={2}>
                          <Text size="sm" fw={500} style={{ color: 'var(--aviary-ink)' }}>
                            {row.name}
                          </Text>
                          {row.campaignName && (
                            <Text size="xs" c="dimmed">{row.campaignName}</Text>
                          )}
                        </Stack>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        variant="light"
                        color="gray"
                        size="sm"
                        leftSection={CHANNEL_ICON[row.channel] || null}
                      >
                        {row.channel}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="sm" style={{ color: 'var(--aviary-ink)' }}>{row.reason}</Text>
                        {row.triggerTurnText && (
                          <Text
                            size="xs"
                            c="dimmed"
                            style={{
                              maxWidth: 280,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            "{row.triggerTurnText}"
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{formatDate(row.closedAt)}</Text>
                    </Table.Td>
                  </UnstyledButton>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>
    </Box>
  );
}
